import { ReactNode, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { Mapping } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Chip, Text, Truncated } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { SETTINGS_TRACKING_IDS } from '../../trackingIds/settings-tracking-ids';
import { ColorDot } from '../appointment-panel/color-dot';
import { CompactTable } from '../compact-table/compact-table';
import { VisibilityToggle } from '../ManagePractitionersV3/VisibilityToggle';
import { ProvidersChip } from './providers-chip';
import { AppointmentTypesTableProps } from './types';

type AppointmentTypesCompactTableProps = AppointmentTypesTableProps;

export const AppointmentTypesCompactTable = ({
  locationId,
  pageConfig,
  isLoading,
  debouncedSearchValue,
  setSelectedAppointmentTypes,
  bulkActions,
  rowActions,
  setPageConfig,
  mappingList,
}: AppointmentTypesCompactTableProps) => {
  const { t } = useTranslation('scheduleAppointmentTypes');
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const isOnlineSchedulingEnabled = getCustomizationFlagValue('onlinescheduling');

  const appointmentTypeInfiniteQuery = SchedulerV3.Helpers.useGetAppointmentTypeInfiniteListForV3({
    searchText: debouncedSearchValue,
    pageLimit: pageConfig.limit,
    locationId,
  });
  const { isLoading: isLoadingAppointmentTypesData } = appointmentTypeInfiniteQuery;
  const handleTableRefetch = useCallback(() => {
    appointmentTypeInfiniteQuery.refetch();
  }, [appointmentTypeInfiniteQuery]);

  const handleSearchField = useCallback(
    (searchValue: string) => {
      const trimmedSearchValue = searchValue.trim();
      if (trimmedSearchValue.length !== 1) {
        setPageConfig((prev) => ({ ...prev, page: 1, searchValue: trimmedSearchValue }));
      }
    },
    [setPageConfig]
  );

  const tableActions = useMemo(
    () => [
      {
        Icon: () => <Icon name='update' size={24} />,
        label: t('Refetch'),
        onClick: handleTableRefetch,
        type: 'icon',
      },
    ],
    [handleTableRefetch, t]
  );

  return (
    <CompactTable
      infiniteQueryProps={appointmentTypeInfiniteQuery}
      isLoading={isLoading || isLoadingAppointmentTypesData}
      tableId='appointment-type-compact-table'
      rowElement={(appointmentType: AppointmentType) => <RowElement appointmentType={appointmentType} />}
      expandedRowElement={(appointmentType: AppointmentType) => (
        <ExpandedRowElement
          appointmentType={appointmentType}
          mappingList={mappingList}
          canUseAppointmentColors={canUseAppointmentColors}
          isOnlineSchedulingEnabled={isOnlineSchedulingEnabled}
        />
      )}
      toolbarElement={<ToolbarElement />}
      rowActions={rowActions}
      bulkActions={bulkActions}
      isSelectable={canUseAppointmentColors}
      isRowDisabled={(row: AppointmentType) => isIntegratedOffice && !row.details?.act}
      rowSelectionConfig={{
        isRowDisabled(rowData) {
          return !rowData.details?.act;
        },
        onSelectionToggle(data) {
          setSelectedAppointmentTypes(data);
        },
      }}
      globalSearchConfig={{
        initialValue: '',
        placeholder: t('Search Appointment Types Name'),
        searchHandler: handleSearchField,
      }}
      tableActions={tableActions}
      {...(!isLoading && {
        emptyStateConfig: {
          type: 'schedule',
          ...(!debouncedSearchValue && {
            header: '',
            description: (
              <div css={{ textAlign: 'center' }}>
                <Text color='subdued'>{t('Ready to start scheduling?')}</Text>
                <Text color='subdued'>{t('Set up your first appointment type to start taking bookings.')}</Text>
              </div>
            ),
          }),
        },
      })}
    />
  );
};

const RowElement = ({ appointmentType }: { appointmentType: AppointmentType }) => {
  return (
    <div css={[styles.flexRow, styles.rowElement]}>
      <div css={styles.displayNameContainer}>
        <CompactTable.SelectRow />
        <Truncated css={styles.displayName}>{appointmentType.displayName}</Truncated>
      </div>
      <div css={styles.rowActions}>
        <CompactTable.RowActions />
        <CompactTable.ExpandRow />
      </div>
    </div>
  );
};

const ExpandedRowElement = ({
  appointmentType,
  mappingList,
  canUseAppointmentColors,
  isOnlineSchedulingEnabled,
}: {
  appointmentType: AppointmentType;
  mappingList: Mapping[];
  canUseAppointmentColors: boolean;
  isOnlineSchedulingEnabled: boolean;
}) => {
  const { t } = useTranslation('scheduleAppointmentTypes');
  const isActive = !!appointmentType.details?.act;
  const duration = appointmentType.details?.dur ? `${appointmentType.details.dur} ${t('Minutes')}` : '-';
  const appointmentTypeId = appointmentType.id;

  return (
    <div css={[styles.flexColumn, styles.expandedRowElement]}>
      {canUseAppointmentColors && (
        <InfoRow
          label={t('Color')}
          value={<ColorDot size='large' color={appointmentType?.details?.clr || PRIMARY_EVENT_COLOR} />}
        />
      )}
      <InfoRow label={t('Duration')} value={duration} />
      <InfoRow
        label={t('Status')}
        value={<Chip variant={isActive ? 'primary' : 'disabled'}>{isActive ? 'Active' : 'Archived'}</Chip>}
      />
      <InfoRow
        label={t('Providers')}
        value={
          <ProvidersChip appointmentTypeId={appointmentTypeId} mappingList={mappingList} css={styles.providersChip} />
        }
      />
      {isOnlineSchedulingEnabled && (
        <InfoRow
          label={t('Online Scheduling')}
          value={
            <VisibilityToggle
              appointmentTypeDetails={appointmentType}
              value={!!appointmentType.details?.sobs}
              disabled={!appointmentType.details?.act}
              trackingId={SETTINGS_TRACKING_IDS.appointmentTypes.onlineSchedulingToggleButton}
            />
          }
        />
      )}
    </div>
  );
};

const InfoRow = ({ label, value }: { label: ReactNode; value: ReactNode }) => {
  return (
    <div css={styles.infoRow}>
      {typeof label === 'string' ? <Text color='subdued'>{label}</Text> : label}
      {typeof value === 'string' ? <Text>{value}</Text> : value}
    </div>
  );
};

const ToolbarElement = () => {
  return (
    <div css={[styles.flexColumn, styles.toolbar]}>
      <div css={[styles.flexRow, styles.searchBarRow]}>
        <CompactTable.Search />
        <CompactTable.TableActions />
      </div>
      <div css={[styles.flexRow, styles.selectAllContainer]}>
        <div css={styles.selectAll}>
          <CompactTable.SelectAll css={styles.selectAllCheckbox} />
          <CompactTable.BulkActions />
        </div>
        <CompactTable.ExpandAll css={styles.expandAllButton} />
      </div>
    </div>
  );
};

const styles = {
  flexRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(1)};
  `,
  flexColumn: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  rowElement: css``,
  expandedRowElement: css`
    padding: ${theme.spacing(2)};
  `,
  toolbar: css`
    gap: ${theme.spacing(3)};
    margin-bottom: ${theme.spacing(3)};
    margin-top: ${theme.spacing(3)};
  `,
  searchBarRow: css`
    padding: ${theme.spacing(0)};
  `,
  selectAllContainer: css`
    align-items: flex-start;
    padding: ${theme.spacing(0, 0, 0, 1)};
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
  `,
  selectAll: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  selectAllCheckbox: css`
    padding: ${theme.spacing(1, 0)};
  `,
  expandAllButton: css`
    margin-top: ${theme.spacing(0.25)};
    padding-left: ${theme.spacing(0.5)};
  `,
  rowActions: css`
    display: flex;
    gap: ${theme.spacing(1)};
  `,
  displayNameContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(3)};
  `,
  displayName: css`
    max-width: 176px;
    font-weight: ${theme.font.weight.bold};
  `,
  infoRow: css`
    display: flex;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
  `,
  providersChip: css`
    justify-content: flex-end;
  `,
};
